import { forwardRef } from 'react';
import type { ReactNode, Ref, PropsWithoutRef, ElementType } from 'react';
import styled, { css } from 'styled-components';

import Grid from '../Grid';
import type { GridContainerProps } from '../Grid';
import { defaultThemeProp } from '../../theme';
import type { ForwardRefForwardPropsComponent, NoChildrenProp, TestIdProp } from '../../types';
import { StyledProgressRing } from '../Progress';
import { useTestIds } from '../../hooks';
import { withTestIds } from '../../utils';

import { getSummaryItemTestIds } from './SummaryItem.test-ids';

export interface SummaryItemProps extends NoChildrenProp, TestIdProp {
  /** Region for the primary information. */
  primary: NonNullable<ReactNode>;
  /** Region for displaying supporting metadata. */
  secondary?: ReactNode;
  /**
   * Determines layout structure.
   * @default 'stacked'
   */
  layout?: 'stacked' | 'inline';
  /** Region for an Avatar, Icon, or other supporting visual. */
  visual?: ReactNode;
  /** Region for adding Actions, Buttons, or Links. */
  actions?: ReactNode;
  /** Override the default Grid container props on SummaryItem. */
  container?: GridContainerProps;
  /**
   * Determines how the text within primary and secondary fields should handle overflow.
   * @default 'wrap'
   */
  overflowStrategy?: 'wrap' | 'ellipsis';
  /** HTML tag to render the SummaryItem's element with. */
  as?: ElementType;
  /** Ref for the SummaryItem. */
  ref?: Ref<HTMLElement>;
}

export const StyledVisual = styled.div`
  > ${StyledProgressRing}, img {
    display: block;
    object-fit: cover;
    width: 2rem;
    height: 2rem;
    max-width: 100%;
    max-height: 100%;
  }
`;

StyledVisual.defaultProps = defaultThemeProp;

export const StyledPrimary = styled(Grid)<{
  isString?: boolean;
  overflowStrategy?: SummaryItemProps['overflowStrategy'];
}>(({ theme, isString, overflowStrategy }) => {
  return css`
    ${overflowStrategy === 'ellipsis'
      ? css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-break: break-word;
        `}

    ${isString &&
    css`
      font-weight: ${theme.base['font-weight']['semi-bold']};
    `}
  `;
});

StyledPrimary.defaultProps = defaultThemeProp;

export const StyledSecondary = styled(Grid)<{
  overflowStrategy?: SummaryItemProps['overflowStrategy'];
}>(({ overflowStrategy }) => {
  return css`
    ${overflowStrategy === 'ellipsis'
      ? css`
          overflow: hidden;
          text-overflow: ellipsis;
        `
      : css`
          word-break: break-word;
        `}
  `;
});

export const StyledSummaryItemActions = styled.div`
  white-space: nowrap;
`;

export const StyledSummaryItem = styled.div``;

const getColumns = ({ visual, actions }: Pick<SummaryItemProps, 'visual' | 'actions'>) => {
  return `${visual ? 'auto ' : ''}minmax(0, 1fr)${actions ? ' auto' : ''}`;
};

const getAreas = ({
  secondary,
  layout,
  visual,
  actions
}: Pick<SummaryItemProps, 'secondary' | 'layout' | 'visual' | 'actions'>) => {
  return `"${visual ? 'visual ' : ''}primary${secondary && layout === 'inline' ? ' secondary' : ''}${actions ? ' actions' : ''}"${
    secondary && layout === 'stacked'
      ? `\n"${visual ? 'visual ' : ''}secondary${actions ? ' actions' : ''}"`
      : ''
  }`;
};

const SummaryItem: ForwardRefForwardPropsComponent<SummaryItemProps> = forwardRef(
  function SummaryItem(
    {
      testId,
      visual,
      primary,
      secondary,
      layout = 'stacked',
      actions,
      container,
      overflowStrategy = 'wrap',
      as,
      ...restProps
    }: PropsWithoutRef<SummaryItemProps>,
    ref: SummaryItemProps['ref']
  ) {
    const testIds = useTestIds(testId, getSummaryItemTestIds);

    return (
      <Grid
        data-testid={testIds.root}
        {...restProps}
        ref={ref}
        container={{
          cols: getColumns({ visual, actions }),
          colGap: 1,
          areas: getAreas({ secondary, layout, visual, actions }),
          ...container
        }}
        as={StyledSummaryItem}
        forwardedAs={as}
      >
        {visual && (
          <Grid
            data-testid={testIds.visual}
            as={StyledVisual}
            item={{ area: 'visual', alignSelf: 'center' }}
          >
            {visual}
          </Grid>
        )}
        <StyledPrimary
          data-testid={testIds.primary}
          item={{
            area: 'primary',
            alignSelf: secondary && layout === 'stacked' ? 'end' : 'center'
          }}
          isString={typeof primary === 'string'}
          overflowStrategy={overflowStrategy}
        >
          {primary}
        </StyledPrimary>
        {secondary && (
          <StyledSecondary
            data-testid={testIds.secondary}
            item={{
              area: 'secondary',
              alignSelf: layout === 'stacked' ? 'start' : 'center'
            }}
            overflowStrategy={overflowStrategy}
          >
            {secondary}
          </StyledSecondary>
        )}
        {actions && (
          <Grid
            data-testid={testIds.actions}
            as={StyledSummaryItemActions}
            item={{ area: 'actions', alignSelf: 'center' }}
          >
            {actions}
          </Grid>
        )}
      </Grid>
    );
  }
);

export default withTestIds(SummaryItem, getSummaryItemTestIds);
